import { SourceType } from '@kanbu/schema/enums';

/**
 * Managed-source predicates (master plan §3.2), generalizing the website-only
 * `isWebsiteManaged` checks so Drive (K2) and RSS (K3) roots inherit the same
 * lock, badge and status treatment without touching call sites.
 */

/** The subset of folder fields these predicates read. */
export type ManagedSourceFolder = {
  sourceType?: SourceType | string | null;
  sourceUri?: string | null;
};

/**
 * True for a managed root *and* everything discovery created beneath it — the
 * predicate that locks structure (no move/paste/create).
 *
 * Robust form (§3.2): a folder belongs to a managed tree iff its `sourceType`
 * is not `upload`. Every adapter stamps the root's `sourceType` on each
 * subfolder it creates (website via `ensureFolderHierarchy`; Drive/RSS inherit
 * the same path), so this alone marks subtree membership — including Drive/RSS
 * subfolders, which carry no `sourceUri`.
 */
export function isManagedSource(
  folder: ManagedSourceFolder | null | undefined,
): boolean {
  return (
    !!folder && !!folder.sourceType && folder.sourceType !== SourceType.Upload
  );
}

/**
 * A managed source *root*: carries the source's external identity
 * (`sourceUri`) and therefore owns the sync settings, status and "Sync now"
 * action. Subfolders below it are managed (see {@link isManagedSource}) but are
 * not roots.
 */
export function isManagedSourceRoot(
  folder: ManagedSourceFolder | null | undefined,
): boolean {
  return !!folder && folder.sourceUri != null;
}

/**
 * A website root specifically — the only source type with its own
 * non-inherited `discoveryFrequency` ("look for new pages") schedule,
 * distinct from `updateFrequency` (content re-check, inherited like every
 * other auto-update setting and applicable to any managed folder).
 */
export function isWebsiteDiscoveryRoot(
  folder: ManagedSourceFolder | null | undefined,
): boolean {
  return (
    !!folder &&
    folder.sourceType === SourceType.Website &&
    isManagedSourceRoot(folder)
  );
}
